import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import InterviewProcessSection from './sections/InterviewProcessSection';
import HowJumpstartWorksSection from './sections/HowJumpstartWorksSection';
import SetupForSuccessSection from './sections/SetupForSuccessSection';
import StandoutApplicationsSection from './sections/StandoutApplicationsSection';
import StandOutSection from './sections/StandOutSection';
import CaseStudySection from './sections/CaseStudySection';
import InterviewSupportSection from './sections/InterviewSupportSection';
import FinalInterviewSection from './sections/FinalInterviewSection';
import OffersEndgameSection from './sections/OffersEndgameSection';
import SalesResourcesHub from './sales_resources_hub';
import MarketingResourcesHub from './marketing_resources_hub';
import { useAuthRedirect } from '../../hooks/useAuthRedirect';
import { startSessionReplay, stopSessionReplay, trackEvent } from '../../lib/analytics';

const ResourcesHub = () => {
  const [currentSection, setCurrentSection] = useState('main');
  const [completedSections, setCompletedSections] = useState([]);
  // Add stream checking state
  const [candidateStream, setCandidateStream] = useState("");
  const [streamLoading, setStreamLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Ensure user is authenticated
  const { isAuthenticated, isChecking } = useAuthRedirect();

  // Check candidate stream type
  const getStreamType = () => {
    const streamLower = candidateStream.toLowerCase();
    if (streamLower.includes("sales") || streamLower.includes("commercial associate")) {
      return "sales";
    } else if (streamLower.includes("marketing")) {
      return "marketing";
    } else if (streamLower.includes("generalist")) {
      return "generalist";
    }
    return "other";
  };

  // Helper functions for interview coaching
  const getCalendlyLink = (sessionType) => {
    const streamType = getStreamType();
    if (streamType === "sales") {
      switch (sessionType) {
        case 'first':
          return 'https://calendly.com/d/cv4g-cnm-zrd/jumpstart-sales-first-interview-prep-call';
        case 'second':
          return 'https://calendly.com/d/cr5q-bx7-235/jumpstart-sales-role-play-practise-call';
        case 'final':
          return 'https://calendly.com/kabir-7/catch-up-regarding-jumpstart?month=2025-07';
        default:
          return 'https://calendly.com/d/cv4g-cnm-zrd/jumpstart-sales-first-interview-prep-call';
      }
    } else {
      // Generalist and Marketing
      switch (sessionType) {
        case 'first':
          return 'https://calendly.com/d/cv8m-cj2-9qz/jumpstart-generalists-first-interview-prep-call?month=2025-07';
        case 'second':
          return 'https://calendly.com/d/csnj-cfr-68s/jumpstart-generalist-2nd-round-support';
        case 'final':
          return 'https://calendly.com/kabir-7/catch-up-regarding-jumpstart?month=2025-07';
        default:
          return 'https://calendly.com/d/cv8m-cj2-9qz/jumpstart-generalists-first-interview-prep-call?month=2025-07';
      }
    }
  };

  const getSessionDescription = (sessionType) => {
    const streamType = getStreamType();
    if (streamType === "sales") {
      switch (sessionType) {
        case 'first':
          return {
            title: "First Interview Prep (Mandatory)",
            description: "Your elevator pitch, essential prep tips, and Q&A time",
            coach: "Gemma or Channon"
          };
        case 'second':
          return {
            title: "Role Play Practice (Optional)",
            description: "Prepare for roleplays and case studies",
            coach: "Gemma or Channon"
          };
        case 'final':
          return {
            title: "Final Round Support (Optional)",
            description: "Polish your performance for the final round",
            coach: "Kabir"
          };
      }
    } else {
      // Generalist and Marketing
      switch (sessionType) {
        case 'first':
          return {
            title: "First Interview Prep (Mandatory)",
            description: "Your elevator pitch, essential prep tips, and Q&A time",
            coach: "Edward"
          };
        case 'second':
          return {
            title: "Second Round Support (Optional)",
            description: "Extra guidance before your second round",
            coach: "Naim"
          };
        case 'final':
          return {
            title: "Final Round Support (Optional)",
            description: "Polish your performance for the final round",
            coach: "Kabir"
          };
      }
    }
  };

  const sections = [
    {
      id: 'how-jumpstart-works',
      title: 'How Jumpstart Works',
      description: 'Understand roles vs match requests, deadlines, and weekly cadence.',
      icon: '📅',
      estimatedTime: '8 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'setup-for-success',
      title: 'Setting yourself up for success',
      description: 'Polish your profile, level up your CV, and create a standout video intro.',
      icon: '🧭',
      estimatedTime: '15 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'interview-process',
      title: 'How the typical Generalist interview process works',
      description: 'Whilst every startup is different, most follow a 3-stage structure.',
      icon: '🎯',
      estimatedTime: '15 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'stand-out',
      title: 'How to stand out in your 1st interview',
      description: 'Master the art of first impressions and position yourself as the ideal candidate.',
      icon: '⭐',
      estimatedTime: '20 min read',
      difficulty: 'Intermediate'
    },
    {
      id: 'case-study',
      title: 'Nailing the take-home case study',
      description: 'Turn your case study into a showcase of your strategic thinking and execution skills.',
      icon: '📊',
      estimatedTime: '25 min read',
      difficulty: 'Advanced'
    },
    {
      id: 'standout-applications',
      title: 'How to Make Standout Applications',
      description: 'Tips to write winning rationales, use startup video pitches and smart outreach.',
      icon: '✍️',
      estimatedTime: '10 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'interview-support',
      title: 'Using Jumpstart’s 1-on-1 interview support network',
      description: 'Jumpstart gives you access to tailored interview prep — make use of it early and often.',
      icon: '🤝',
      estimatedTime: '10 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'final-interview',
      title: 'Getting over the line in the final interview',
      description: 'Navigate culture fit and self-awareness questions to close the deal.',
      icon: '🏆',
      estimatedTime: '18 min read',
      difficulty: 'Intermediate'
    },
    {
      id: 'offers-endgame',
      title: 'Endgame',
      description: 'Navigate offers, negotiate terms, and make the right career decision.',
      icon: '🎉',
      estimatedTime: '12 min read',
      difficulty: 'Advanced'
    }
  ];

  // Initialize section from URL param and react to URL changes
  useEffect(() => {
    const sectionParam = searchParams.get('section');
    const sectionIds = sections.map(s => s.id);
    if (!sectionParam) {
      if (currentSection !== 'main') {
        setCurrentSection('main');
      }
      return;
    }
    if (sectionIds.includes(sectionParam) && currentSection !== sectionParam) {
      setCurrentSection(sectionParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setSection = (sectionId) => {
    setCurrentSection(sectionId);
    try {
      const params = new URLSearchParams(searchParams);
      if (sectionId === 'main') {
        if (params.has('section')) {
          params.delete('section');
          setSearchParams(params, { replace: false });
        }
      } else {
        if (params.get('section') !== sectionId) {
          params.set('section', sectionId);
          setSearchParams(params, { replace: false });
        }
      }
    } catch (_) {}
  };

  // Activate Session Replay for this page
  useEffect(() => {
    try { startSessionReplay(); } catch (_) {}
    return () => { try { stopSessionReplay(); } catch (_) {} };
  }, []);

  // Fetch candidate stream and progress
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const fetchCandidateData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setStreamLoading(false);
          return;
        }

        const res = await axios.get('/api/candidates/fetchCandidate', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data && res.data.candidate) {
          const candidateData = res.data.candidate.fields;
          
          // Get candidate stream (same logic as roles_list.js)
          let streamValue = candidateData["Do not edit - new Stream (for visibility column)"] || "";
          if (Array.isArray(streamValue)) {
            streamValue = streamValue.length > 0 ? streamValue[0] : "";
          }
          setCandidateStream(streamValue);
          
          // Get progress data
          const completed = sections
            .filter(section => candidateData[section.id])
            .map(section => section.id);
          
          setCompletedSections(completed);
        }
      } catch (error) {
        console.error("Error fetching candidate data:", error);
      } finally {
        setStreamLoading(false);
      }
    };

    fetchCandidateData();
  }, [isAuthenticated, isChecking]);

  const markAsCompleted = async (sectionId) => {
    if (completedSections.includes(sectionId)) return;

    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("No token found");
        return;
      }
      
      await axios.patch('/api/candidates/updateCandidate', 
        { fields: { [sectionId]: true } },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updated = [...completedSections, sectionId];
      setCompletedSections(updated);
    } catch (error) {
      console.error("Error updating candidate progress:", error);
    }
  };

  // Navigation helper
  const getNextSection = (currentId) => {
    const sectionIds = sections.map(section => section.id);
    const currentIndex = sectionIds.indexOf(currentId);
    return currentIndex < sectionIds.length - 1 ? sectionIds[currentIndex + 1] : null;
  };

  const goToNextSection = (currentId) => {
    const nextSectionId = getNextSection(currentId);
    if (nextSectionId) {
      setSection(nextSectionId);
    } else {
      setSection('main');
    }
    // Scroll to top of page when navigating to next section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSectionContent = () => {
    const getSectionTitle = (id) => sections.find(s => s.id === id)?.title || '';
    const nextSectionId = getNextSection(currentSection);
    const nextSectionTitle = nextSectionId ? getSectionTitle(nextSectionId) : null;

    const commonProps = {
      onBack: () => setSection('main'),
      onNext: () => goToNextSection(currentSection),
      nextSectionTitle,
      hasNext: !!nextSectionId
    };

    switch (currentSection) {
      case 'how-jumpstart-works':
        return (
          <HowJumpstartWorksSection
            {...commonProps}
            onComplete={() => markAsCompleted('how-jumpstart-works')}
            isCompleted={completedSections.includes('how-jumpstart-works')}
          />
        );
      case 'setup-for-success':
        return (
          <SetupForSuccessSection
            {...commonProps}
            onComplete={() => markAsCompleted('setup-for-success')}
            isCompleted={completedSections.includes('setup-for-success')}
            streamType={streamType}
            exampleVideos={[
              { name: 'Natasha', url: 'https://www.loom.com/share/ae43c5b099b242c698d4a063c9a98852?sid=e242de45-1704-4819-8279-59b27a83ccf2', creator: 'Uses past experiences to convey passion with a natural tone' }
            ]}
          />
        );
      case 'interview-process':
        return (
          <InterviewProcessSection 
            {...commonProps}
            onComplete={() => markAsCompleted('interview-process')}
            isCompleted={completedSections.includes('interview-process')}
          />
        );
      case 'stand-out':
        return (
          <StandOutSection 
            {...commonProps}
            onComplete={() => markAsCompleted('stand-out')}
            isCompleted={completedSections.includes('stand-out')}
          />
        );
      case 'case-study':
        return (
          <CaseStudySection 
            {...commonProps}
            onComplete={() => markAsCompleted('case-study')}
            isCompleted={completedSections.includes('case-study')}
          />
        );
      case 'standout-applications':
        return (
          <StandoutApplicationsSection 
            {...commonProps}
            onComplete={() => markAsCompleted('standout-applications')}
            isCompleted={completedSections.includes('standout-applications')}
          />
        );
      case 'interview-support':
        return (
          <InterviewSupportSection 
            {...commonProps}
            onComplete={() => markAsCompleted('interview-support')}
            isCompleted={completedSections.includes('interview-support')}
          />
        );
      case 'final-interview':
        return (
          <FinalInterviewSection 
            {...commonProps}
            onComplete={() => markAsCompleted('final-interview')}
            isCompleted={completedSections.includes('final-interview')}
          />
        );
      case 'offers-endgame':
        return (
          <OffersEndgameSection 
            {...commonProps}
            onComplete={() => markAsCompleted('offers-endgame')}
            isCompleted={completedSections.includes('offers-endgame')}
          />
        );
      default:
        return null;
    }
  };

  if (streamLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200"
        >
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 border-4 border-[#02B491] border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading Resources</h2>
          <p className="text-lg text-gray-600 max-w-md">
            Please wait while we check your candidate stream and load the resources.
          </p>
        </motion.div>
      </div>
    );
  }

  const streamType = getStreamType();
  
  // Route to appropriate stream-specific hub
  if (streamType === "sales") {
    return <SalesResourcesHub />;
  }
  
  if (streamType === "marketing") {
    return <MarketingResourcesHub />;
  }
  
  if (streamType === "other") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center p-8 sm:p-12 bg-white rounded-xl shadow-lg border border-gray-200 max-w-2xl mx-4"
        >
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Resources Coming Soon</h2>
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">
            We're working hard to bring you tailored interview resources for your stream. 
          </p>
          <p className="text-base text-gray-500 mb-8">
            Currently, interview resources are available for <span className="font-semibold text-gray-700">Generalist, Marketing, and Sales</span> candidates. 
            Resources for other streams will be released soon.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-[#02B491] text-white rounded-lg hover:bg-[#029078] transition-colors font-medium"
            >
              Go Back
            </button>
            <a
              href="mailto:info@jumpstart-uk.com"
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Contact Support
            </a>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // Continue with Generalist/Marketing resources (existing content below)

  if (currentSection !== 'main') {
    return renderSectionContent();
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Compact Header */}
      <div className="w-full bg-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Interview Resources
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Master every stage of the startup interview process with our comprehensive guides and expert insights.
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Compact Progress Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        
        {/* Compact coaching banner */}
        <div className="mb-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#02B491] text-white rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">🎓</span>
              <div>
                <h3 className="font-semibold text-sm">Book 1-on-1 interview prep sessions</h3>
                <p className="text-xs text-white/90">Get personalised coaching from our expert interview specialists</p>
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href={getCalendlyLink('first')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white text-[#02B491] rounded-md hover:bg-gray-50 transition-colors"
              >
                Book First Interview Prep
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <a
                href={getCalendlyLink('second')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 text-white border border-white/20 rounded-md hover:bg-white/20 transition-colors"
              >
                Second Round Prep
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <a
                href={getCalendlyLink('final')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 text-white border border-white/20 rounded-md hover:bg-white/20 transition-colors"
              >
                Final Round Prep
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Compact Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sections.map((section, index) => {
            const isCompleted = completedSections.includes(section.id);
            
            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl border-2 transition-all duration-300 hover:shadow-lg cursor-pointer group relative overflow-hidden ${
                  isCompleted 
                    ? 'border-[#02B491] shadow-md' 
                    : 'border-gray-100 hover:border-[#02B491]/50 shadow-sm hover:shadow-md'
                }`}
                onClick={() => {
                  try {
                    trackEvent('Resource Card Clicked', {
                      card_id: section.id,
                      card_title: section.title,
                      hub: 'resources',
                    });
                  } catch (_) {}
                  setSection(section.id);
                }}
              >
                <div className="p-6">
                  {/* Header with Icon and Title */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-2xl">{section.icon}</div>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#02B491] transition-colors">
                      {section.title}
                    </h3>
                    {isCompleted && (
                      <div className="ml-auto">
                        <div className="w-6 h-6 bg-[#02B491] rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {section.description}
                  </p>

                  {/* Start Button with Arrow */}
                  <div className="flex items-center justify-between">
                    <span className="text-[#02B491] font-semibold group-hover:text-[#029078] transition-colors">
                      Start
                    </span>
                    <svg 
                      className="w-5 h-5 text-[#02B491] group-hover:text-[#029078] group-hover:translate-x-1 transition-all duration-200" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>

                {/* Gradient overlay for completed sections */}
                {isCompleted && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#02B491]/5 to-transparent pointer-events-none" />
                )}
              </motion.div>
            );
          })}
                      </div>
                      
      </div>
    </div>
  );
};

export default ResourcesHub; 